import { useMemo, useState } from 'react';
import { Card, Badge, Button, Input } from '@azyntor-labs/nexus-ui';
import {
  CheckCircle,
  XCircle,
  Clock,
  AlertTriangle,
  ChevronLeft,
  ChevronRight,
  Search,
} from 'lucide-react';
import type { AttemptFullDetailAnswer, AttemptFullDetailTimingStats } from '@/api/endpoints';

interface Props {
  answers: AttemptFullDetailAnswer[];
  timing: AttemptFullDetailTimingStats;
}

type FilterKey = 'all' | 'correct' | 'wrong' | 'unanswered' | 'flagged';
const PAGE_SIZE = 10;

const anomalyLabel: Record<string, string> = {
  suspiciously_fast: '<3s',
  stalled: '>5min',
  blank: 'blank',
  unanswered: 'unanswered',
};

function fmtSeconds(s: number) {
  if (!s || s < 1) return '—';
  if (s < 60) return `${s}s`;
  const m = Math.floor(s / 60);
  const rem = s % 60;
  return rem ? `${m}m ${rem}s` : `${m}m`;
}

export function QuestionsBreakdown({ answers, timing }: Props) {
  const [filter, setFilter] = useState<FilterKey>('all');
  const [search, setSearch] = useState('');
  const [page, setPage] = useState(1);

  // Precompute bucket counts for tabs
  const counts = useMemo(() => {
    let correct = 0;
    let wrong = 0;
    let unanswered = 0;
    let flagged = 0;
    for (const a of answers) {
      if (a.is_correct === true) correct++;
      else if (a.is_correct === false) wrong++;
      if (a.anomalies?.includes('unanswered') || a.anomalies?.includes('blank')) unanswered++;
      if (a.anomalies && a.anomalies.some((x) => x !== 'unanswered' && x !== 'blank')) flagged++;
    }
    return { all: answers.length, correct, wrong, unanswered, flagged };
  }, [answers]);

  const filtered = useMemo(() => {
    let rows = answers;
    if (filter === 'correct') rows = rows.filter((a) => a.is_correct === true);
    else if (filter === 'wrong') rows = rows.filter((a) => a.is_correct === false);
    else if (filter === 'unanswered')
      rows = rows.filter((a) => a.anomalies?.includes('unanswered') || a.anomalies?.includes('blank'));
    else if (filter === 'flagged')
      rows = rows.filter((a) => a.anomalies && a.anomalies.some((x) => x !== 'unanswered' && x !== 'blank'));
    if (search.trim()) {
      const q = search.toLowerCase();
      rows = rows.filter((a) => a.question_text.toLowerCase().includes(q));
    }
    return rows;
  }, [answers, filter, search]);

  const totalPages = Math.max(1, Math.ceil(filtered.length / PAGE_SIZE));
  const safePage = Math.min(page, totalPages);
  const paged = filtered.slice((safePage - 1) * PAGE_SIZE, safePage * PAGE_SIZE);

  if (!answers?.length) {
    return (
      <Card>
        <div className="p-6 text-center text-white/40 text-sm">No questions on this exam yet.</div>
      </Card>
    );
  }

  const FilterTab = ({ k, label, count, tone }: { k: FilterKey; label: string; count: number; tone?: string }) => (
    <button
      onClick={() => {
        setFilter(k);
        setPage(1);
      }}
      className={[
        'px-3 py-1.5 rounded-lg text-xs font-medium transition border inline-flex items-center gap-2',
        filter === k
          ? tone || 'bg-teal-500/15 border-teal-400/40 text-teal-200'
          : 'bg-white/[0.03] border-white/[0.06] text-white/60 hover:bg-white/[0.06] hover:text-white/80',
      ].join(' ')}
    >
      {label}
      <span
        className={[
          'px-1.5 py-0.5 rounded text-[10px] font-mono',
          filter === k ? 'bg-black/20' : 'bg-white/5',
        ].join(' ')}
      >
        {count}
      </span>
    </button>
  );

  return (
    <Card>
      {/* Header */}
      <div className="p-5 border-b border-white/5">
        <div className="flex items-start justify-between flex-wrap gap-3">
          <div>
            <h2 className="text-lg font-semibold text-white">Question breakdown</h2>
            <p className="text-xs text-white/40 mt-1">
              Answered {timing.answered_count}/{timing.total_questions} · avg{' '}
              {timing.avg_seconds_per_answer}s · median {fmtSeconds(timing.median_seconds)} ·{' '}
              <span className={timing.fast_answer_count > 0 ? 'text-red-300' : ''}>
                {timing.fast_answer_count} fast (&lt;3s)
              </span>
            </p>
          </div>
          <Input
            placeholder="Search question text…"
            value={search}
            onChange={(e) => {
              setSearch(e.target.value);
              setPage(1);
            }}
            size="sm"
            leftIcon={<Search className="w-3.5 h-3.5" />}
            className="w-56"
          />
        </div>

        {/* Filter tabs */}
        <div className="flex flex-wrap gap-2 mt-4">
          <FilterTab k="all" label="All" count={counts.all} />
          <FilterTab
            k="correct"
            label="Correct"
            count={counts.correct}
            tone="bg-emerald-500/15 border-emerald-400/40 text-emerald-200"
          />
          <FilterTab
            k="wrong"
            label="Wrong"
            count={counts.wrong}
            tone="bg-red-500/15 border-red-400/40 text-red-200"
          />
          <FilterTab
            k="unanswered"
            label="Unanswered"
            count={counts.unanswered}
            tone="bg-white/10 border-white/20 text-white/80"
          />
          <FilterTab
            k="flagged"
            label="Anomalies"
            count={counts.flagged}
            tone="bg-amber-500/15 border-amber-400/40 text-amber-200"
          />
        </div>
      </div>

      {/* List */}
      <div className="divide-y divide-white/[0.05]">
        {paged.length === 0 && (
          <div className="p-8 text-center text-sm text-white/40">No questions match this filter.</div>
        )}
        {paged.map((a, idxInPage) => {
          const absIdx = (safePage - 1) * PAGE_SIZE + idxInPage;
          const correct = a.is_correct === true;
          const wrong = a.is_correct === false;
          const pickedLabels = a.selected_labels || [];
          const correctLabels = a.correct_labels || [];
          return (
            <div key={a.question_id} className="p-4 hover:bg-white/[0.02]">
              <div className="flex items-start gap-3">
                <div className="text-xs text-white/30 font-mono shrink-0 w-10 pt-0.5">
                  Q{absIdx + 1}
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-start justify-between gap-3 mb-2">
                    <p className="text-sm text-white/90 leading-5">
                      {a.question_text || '(no text)'}
                    </p>
                    <div className="shrink-0 flex items-center gap-2">
                      {correct && (
                        <Badge variant="success" size="sm">
                          <CheckCircle className="w-3 h-3 mr-1" />
                          Correct
                        </Badge>
                      )}
                      {wrong && (
                        <Badge variant="danger" size="sm">
                          <XCircle className="w-3 h-3 mr-1" />
                          Wrong
                        </Badge>
                      )}
                      {a.is_correct === undefined && (
                        <Badge variant="secondary" size="sm">
                          Pending
                        </Badge>
                      )}
                    </div>
                  </div>

                  {/* Options */}
                  {a.options?.length > 0 && (
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-1.5 mt-2">
                      {a.options.map((o) => {
                        const classes = [
                          'px-2.5 py-1.5 rounded text-xs border flex items-center justify-between gap-2',
                        ];
                        if (o.is_correct && o.is_picked) {
                          classes.push(
                            'bg-emerald-500/10 border-emerald-500/40 text-emerald-300',
                          );
                        } else if (o.is_correct) {
                          classes.push(
                            'bg-emerald-500/5 border-emerald-500/30 text-emerald-300',
                          );
                        } else if (o.is_picked) {
                          classes.push('bg-red-500/10 border-red-500/40 text-red-300');
                        } else {
                          classes.push('bg-white/[0.02] border-white/[0.06] text-white/60');
                        }
                        return (
                          <div key={o.id} className={classes.join(' ')}>
                            <span className="truncate">{o.text}</span>
                            <span className="text-[10px] uppercase tracking-wider opacity-70 shrink-0">
                              {o.is_picked && o.is_correct
                                ? 'picked · correct'
                                : o.is_picked
                                ? 'picked'
                                : o.is_correct
                                ? 'correct'
                                : ''}
                            </span>
                          </div>
                        );
                      })}
                    </div>
                  )}

                  {/* Text/code answer fallback */}
                  {(!a.options || a.options.length === 0) && a.text_answer && (
                    <pre className="mt-2 p-2 rounded bg-black/30 border border-white/[0.06] text-xs text-white/80 overflow-x-auto whitespace-pre-wrap">
                      {a.text_answer}
                    </pre>
                  )}
                  {(!a.options || a.options.length === 0) && !a.text_answer && pickedLabels.length > 0 && (
                    <p className="mt-2 text-xs text-white/60">
                      Student answered: <span className="text-white">{pickedLabels.join(', ')}</span>
                    </p>
                  )}
                  {wrong && pickedLabels.length === 0 && correctLabels.length > 0 && (
                    <p className="mt-2 text-xs text-white/50">
                      Expected: <span className="text-emerald-300">{correctLabels.join(', ')}</span>
                    </p>
                  )}

                  {/* Meta row */}
                  <div className="flex items-center flex-wrap gap-3 mt-2.5 text-[11px] text-white/40">
                    <span className="inline-flex items-center gap-1">
                      <Clock className="w-3 h-3" /> {fmtSeconds(a.time_taken_seconds)}
                    </span>
                    <span>
                      Points: <span className="text-white/70">{a.score ?? 0}</span> / {a.points}
                    </span>
                    {a.anomalies?.map((x) => (
                      <span
                        key={x}
                        className="inline-flex items-center gap-1 text-amber-300/80"
                      >
                        <AlertTriangle className="w-3 h-3" /> {anomalyLabel[x] || x}
                      </span>
                    ))}
                  </div>
                </div>
              </div>
            </div>
          );
        })}
      </div>

      {/* Pagination */}
      {filtered.length > PAGE_SIZE && (
        <div className="p-4 border-t border-white/5 flex items-center justify-between text-xs text-white/50">
          <div>
            Showing <span className="text-white/80">{(safePage - 1) * PAGE_SIZE + 1}</span>–
            <span className="text-white/80">
              {Math.min(safePage * PAGE_SIZE, filtered.length)}
            </span>{' '}
            of <span className="text-white/80">{filtered.length}</span>
          </div>
          <div className="flex items-center gap-2">
            <Button
              variant="ghost"
              size="sm"
              disabled={safePage === 1}
              onClick={() => setPage((p) => Math.max(1, p - 1))}
              leftIcon={<ChevronLeft className="w-3.5 h-3.5" />}
            >
              Prev
            </Button>
            <span className="font-mono text-white/70">
              {safePage} / {totalPages}
            </span>
            <Button
              variant="ghost"
              size="sm"
              disabled={safePage >= totalPages}
              onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
              rightIcon={<ChevronRight className="w-3.5 h-3.5" />}
            >
              Next
            </Button>
          </div>
        </div>
      )}
    </Card>
  );
}
